import { CancelToken } from 'axios';
import { isEmpty } from 'lodash';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Box } from '@hivebrite/design-system/Box';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useEventsIndexContext } from 'hb-react/shared/apps/Events/contexts/EventsIndexContext';
import fetchEvent from 'hb-react/shared/apps/Events/requests/fetchEvent';

const LeafletMap = lazy(() =>
  import('hb-react/frontoffice/components/LeafletMap'),
);

const EventsMap = ({
  config,
  currentFilterLocationBounds,
  filters,
  geoBoundingBoxFilter,
  isSearchingOnMapBounds,
  setGeoBoundingBoxFilter,
  setIsSearchingOnMapBounds,
  selectedEventComponent,
}) => {
  const { currentUser } = useDefaultAppContexts();
  const { selectedEvent, setSelectedEvent, refreshMapMarkersRef } =
    useEventsIndexContext();

  const [initialBounds, setInitialBounds] = useState(null);
  const [currentGeoBoundingBox, setCurrentGeoBoundingBox] = useState({});
  const [zoomOnSelectedEvent, setZoomOnSelectedEvent] = useState(true);
  const centerMapRef = useRef(null);

  const fetchMarkersUrlV1 = useMemo(
    () => (data) =>
      filters.map((f) =>
        config.routes.markers_path({
          include_network_events: true,
          cluster_mode: false,
          ...f,
          ...data,
          query: {
            ...(f?.query || {}),
            ...(data?.query || {}),
          },
        }),
      ),
    [filters],
  );
  const fetchMarkersUrlV2 = useMemo(
    () => (data) =>
      filters.map((f) =>
        config.routes.markers_path({
          include_network_events: true,
          cluster_mode: true,
          ...f,
          ...data,
          query: {
            ...(f?.query || {}),
            ...(data?.query || {}),
          },
        }),
      ),
    [filters],
  );

  const cancelTokenRefSelectEvent = useRef(null);
  const cancelRequestSelectEvent = useCallback(
    () => cancelTokenRefSelectEvent.current?.cancel(),
    [],
  );
  const fetchEventWithCancel = (id, type) => {
    cancelRequestSelectEvent();

    cancelTokenRefSelectEvent.current = CancelToken.source('Request canceled');

    return fetchEvent({
      cancelToken: cancelTokenRefSelectEvent.current.token,
      eventId: id,
      routes: {
        event_path:
          type === 'network_events_event'
            ? Routes.event_path
            : (eventId, params) =>
                Routes.user_event_path(currentUser.id, eventId, params),
      },
    });
  };

  const handleMapMove = useCallback(
    ({ geo_bounding_box: geoBoundingBox }) => {
      setCurrentGeoBoundingBox({ geo_bounding_box: geoBoundingBox });

      if (isSearchingOnMapBounds) {
        setGeoBoundingBoxFilter({ geo_bounding_box: geoBoundingBox });
      }
    },
    [setGeoBoundingBoxFilter, isSearchingOnMapBounds],
  );

  // When user ask to search by map position, the current bounds of the map are send as filter
  useEffect(() => {
    if (isSearchingOnMapBounds) {
      setGeoBoundingBoxFilter(currentGeoBoundingBox);
      // No need to clean filter if they are already empty - avoid a re-fetch of the events list
    } else if (!isEmpty(geoBoundingBoxFilter)) {
      setGeoBoundingBoxFilter({});
    }
  }, [isSearchingOnMapBounds]);

  useEffect(() => {
    const northEastLat = currentFilterLocationBounds?.[0]?.[0];
    const northEastLng = currentFilterLocationBounds?.[0]?.[1];
    const southWestLat = currentFilterLocationBounds?.[1]?.[0];
    const southWestLng = currentFilterLocationBounds?.[1]?.[1];

    if (northEastLat && northEastLng && southWestLat && southWestLng) {
      setInitialBounds(currentFilterLocationBounds);
    }
  }, [currentFilterLocationBounds]);

  const handleOnMapClick = () => {
    setSelectedEvent(null);
  };

  useEffect(() => {
    if (!centerMapRef?.current) {
      return;
    }

    let location = selectedEvent?.locations;

    if (Array.isArray(location)) {
      [location] = location;
    }

    const hasLocation = location && location.lat && location.lng;

    if (hasLocation) {
      const { lat, lng } = location;

      if (lat && lng) {
        centerMapRef.current({
          newCenter: [lat, lng],
          newZoom: zoomOnSelectedEvent ? 16 : null, // default maxZoom of LeafletMap
          offsetCenter: true,
        });
      }
    } else if (selectedEvent) {
      centerMapRef.current({
        newCenter: config.mapViewSettings.initialCenter,
        newZoom: config.mapViewSettings.initialZoom,
      });
    }

    // Re-enable centering on the selected event for next time.
    // This is usually disabled temporarily when the user clicked
    // on marker (as opposed to clicking on a card) because we want to
    // maintain the map state for a better UX
    setZoomOnSelectedEvent(true);
  }, [selectedEvent]);

  return (
    <Suspense fallback={null}>
      <Box display="block" position="relative" height="100%" width="100%">
        <LeafletMap
          enableDragging
          enableZoom
          fetchMarkersUrlV1={fetchMarkersUrlV1}
          fetchMarkersUrlV2={fetchMarkersUrlV2}
          handleOnMarkerClick={async ({
            id,
            properties: { event_type: eventType },
          }) => {
            // In this function we can detect if the marker corresponds to an event
            // or if the mark corresponds to a back-end cluster (props.properties.count > 1)
            //
            // if it's a back-end cluster then we can zoom in to the limits of the cluster
            // the limits can be determined base on the geocode (props.properties.geocode)
            // and the precision (pros.properties.precision)
            //
            // NOTE: if the precision is 9 then no need to zoom anymore (we reached the max level)
            // => in that case we need to go and take the users that matched the geocode

            if (id) {
              if (
                !selectedEvent ||
                selectedEvent.id !== id ||
                selectedEvent.type !== eventType
              ) {
                const event = await fetchEventWithCancel(id, eventType);

                setZoomOnSelectedEvent(false);
                setSelectedEvent(event);
              }
            } else {
              setZoomOnSelectedEvent(false);
              setSelectedEvent(null);
            }
          }}
          handleOnMapClick={handleOnMapClick}
          initialBounds={initialBounds}
          initialZoom={config.mapViewSettings.initialZoom}
          initialCenter={config.mapViewSettings.initialCenter}
          handleMapMove={handleMapMove}
          mapCenterOffset={{ x: 0, y: 120 }}
          centerMapRef={centerMapRef}
          refreshMapMarkersRef={refreshMapMarkersRef}
          isSearchingOnMapBounds={isSearchingOnMapBounds}
          onToggleIsSearchingOnMap={() =>
            setIsSearchingOnMapBounds(!isSearchingOnMapBounds)
          }
        />
        {selectedEventComponent}
      </Box>
    </Suspense>
  );
};

export default EventsMap;
